import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from 'react-i18next';
import StaffTaskTile from '@/components/staff/StaffTaskTile';

// ── Icons (sized to match the card icon container) ────────────────────────────

const IconThermometer = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 5a4 4 0 014 4v14.2A8 8 0 1112 28V9a4 4 0 014-4z" stroke="#4F46E5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 25V14" stroke="#4F46E5" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="20" cy="28" r="4" fill="#4F46E5" fillOpacity="0.2" stroke="#4F46E5" strokeWidth="2.2"/>
  </svg>
);

const IconBroom = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 7l18 18" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M23 23l2.5-2.5 7.5 2.5-5 7.5-7.5-5 2.5-2.5z" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="#16A34A" fillOpacity="0.12"/>
    <path d="M28 31c0 1.5-1.5 3-3 3h-8c-1.5 0-3-1.5-3-3v-2.5h14V31z" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="#16A34A" fillOpacity="0.12"/>
  </svg>
);

const IconTruck = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="11" width="20" height="14" rx="2" stroke="#16A34A" strokeWidth="2.2"/>
    <path d="M25 16h5l4 5v4H25V16z" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="#16A34A" fillOpacity="0.1"/>
    <circle cx="11" cy="27" r="3" stroke="#16A34A" strokeWidth="2.2" fill="white"/>
    <circle cx="29" cy="27" r="3" stroke="#16A34A" strokeWidth="2.2" fill="white"/>
    <path d="M15 19l2.5 2.5 5-5" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconTempTracking = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="8" width="6" height="20" rx="3" stroke="#EA580C" strokeWidth="2.2"/>
    <rect x="17" y="14" width="6" height="14" rx="3" stroke="#EA580C" strokeWidth="2.2"/>
    <rect x="28" y="5" width="6" height="23" rx="3" stroke="#EA580C" strokeWidth="2.2"/>
    <circle cx="9" cy="30" r="3" fill="#EA580C" fillOpacity="0.8"/>
    <circle cx="20" cy="30" r="3" fill="#EA580C" fillOpacity="0.5"/>
    <circle cx="31" cy="30" r="3" fill="#EA580C" fillOpacity="0.3"/>
  </svg>
);

const IconChefHat = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 30h14M10 20c0-6 3.5-10 10-10s10 4 10 10c0 3-2 5.5-5 6.5H15c-3-1-5-3.5-5-6.5z" stroke="#EA580C" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="#EA580C" fillOpacity="0.1"/>
    <rect x="13" y="27" width="14" height="4" rx="1.5" stroke="#EA580C" strokeWidth="2" fill="#EA580C" fillOpacity="0.15"/>
    <path d="M20 10v-3M14 12l-2-2M26 12l2-2" stroke="#EA580C" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IconBarcode = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="6" width="5" height="5" rx="1.5" stroke="#1F2937" strokeWidth="2"/>
    <rect x="29" y="6" width="5" height="5" rx="1.5" stroke="#1F2937" strokeWidth="2"/>
    <rect x="6" y="29" width="5" height="5" rx="1.5" stroke="#1F2937" strokeWidth="2"/>
    <path d="M9 14v12M14 11v18M18 14v12M22 11v18M26 14v12M30 11v18" stroke="#1F2937" strokeWidth="2" strokeLinecap="round"/>
    <path d="M14 35h12M14 6h12" stroke="#1F2937" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconOil = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6s-9 8-9 16a9 9 0 0018 0C29 14 20 6 20 6z" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="#D97706" fillOpacity="0.12"/>
    <path d="M15 24a5 5 0 005 5" stroke="#D97706" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M27 13c1.5-1.5 4-1.5 4 1s-2.5 4-4 2.5" stroke="#D97706" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IconWarning = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.67 6.6a2.6 2.6 0 014.66 0l11.5 21.8A2.6 2.6 0 0131.5 32H8.5a2.6 2.6 0 01-2.33-3.6l11.5-21.8z" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="#DC2626" fillOpacity="0.1"/>
    <path d="M20 16v7" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="20" cy="26.5" r="1.5" fill="#DC2626"/>
  </svg>
);

const IconChecklist = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="7" y="6" width="26" height="28" rx="3" stroke="#2563EB" strokeWidth="2.2"/>
    <path d="M13 16l2.5 2.5 5-5" stroke="#2563EB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13 24l2.5 2.5 5-5" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.45"/>
    <path d="M24 14h6M24 22h6M24 29h6" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" opacity="0.35"/>
  </svg>
);

// ── Types ──────────────────────────────────────────────────────────────────────

type TileStatus = 'default' | 'pending' | 'overdue' | 'done';

interface TileConfig {
  id: string;
  icon: React.ReactNode;
  labelKey: string;
  to: string;
  count?: number;
  status: TileStatus;
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function StaffDashboardPage() {
  const apiClient = useApiClient();
  const { t } = useTranslation();

  const REFRESH = 30_000;

  const { data: dashboard, isLoading: dashboardLoading, error: dashboardError, refetch: refetchDashboard } = useQuery({
    queryKey: ['dashboard-staff'],
    queryFn: () => apiClient.getDashboardSummary(),
    refetchInterval: REFRESH,
  });

  const { data: cleaningToday, isLoading: cleaningLoading, error: cleaningError, refetch: refetchCleaning } = useQuery({
    queryKey: ['cleaning-today'],
    queryFn: () => apiClient.getCleaningToday(),
    refetchInterval: REFRESH,
  });

  const equipmentNeedingReadings: number = (dashboard as any)?.equipmentNeedingReadings ?? 0;
  const equipmentOverdue: number = (dashboard as any)?.equipmentOverdue ?? 0;
  const openIncidents: number = (dashboard as any)?.openIncidentsCount ?? 0;

  const cleaningTasks: any[] = (cleaningToday as any)?.tasks ?? [];
  const cleaningPending = cleaningTasks.filter((task) => !task.completedAt).length;
  const cleaningDone = cleaningPending === 0 && cleaningTasks.length > 0;

  // Check for offline/error state
  const isOffline = dashboardError || cleaningError;
  const isLoading = dashboardLoading || cleaningLoading;

  const tempStatus: TileStatus = equipmentOverdue > 0 ? 'overdue' : equipmentNeedingReadings > 0 ? 'pending' : 'done';
  const cleanStatus: TileStatus = cleaningDone ? 'done' : cleaningPending > 0 ? 'pending' : 'default';
  const incidentStatus: TileStatus = openIncidents > 0 ? 'overdue' : 'done';

  const tiles: TileConfig[] = [
    {
      id: 'tasks',
      icon: <IconChecklist />,
      labelKey: 'staffDashboard.tasks',
      to: '/staff/tasks',
      count: (equipmentNeedingReadings + cleaningPending + openIncidents) || undefined,
      status: equipmentOverdue > 0 || openIncidents > 0 ? 'overdue' : (equipmentNeedingReadings + cleaningPending) > 0 ? 'pending' : 'default',
    },
    {
      id: 'temperatures',
      icon: <IconThermometer />,
      labelKey: 'staffDashboard.temperatures',
      to: '/staff/temperatures',
      count: equipmentNeedingReadings || undefined,
      status: tempStatus,
    },
    {
      id: 'cleaning',
      icon: <IconBroom />,
      labelKey: 'staffDashboard.cleaning',
      to: '/staff/cleaning',
      count: cleaningPending || undefined,
      status: cleanStatus,
    },
    {
      id: 'incidents',
      icon: <IconWarning />,
      labelKey: 'staffDashboard.incidents',
      to: '/staff/incidents',
      count: openIncidents || undefined,
      status: incidentStatus,
    },
    {
      id: 'reception',
      icon: <IconTruck />,
      labelKey: 'staffDashboard.reception',
      to: '/staff/reception',
      status: 'default',
    },
    {
      id: 'preparations',
      icon: <IconChefHat />,
      labelKey: 'staffDashboard.preparations',
      to: '/staff/traceability/preparations',
      status: 'default',
    },
    {
      id: 'traceability',
      icon: <IconBarcode />,
      labelKey: 'staffDashboard.traceability',
      to: '/staff/traceability',
      status: 'default',
    },
    {
      id: 'temp-tracking',
      icon: <IconTempTracking />,
      labelKey: 'staffDashboard.tempTracking',
      to: '/staff/temp-tracking',
      status: 'default',
    },
    {
      id: 'oil',
      icon: <IconOil />,
      labelKey: 'staffDashboard.oil',
      to: '/staff/oil',
      status: 'default',
    },
  ];

  return (
    <div>
      {/* Offline/Error State */}
      {isOffline && (
        <div className="mb-6 p-4 sm:p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16H5a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v8m-7-4h.01M9 20h6" />
            </svg>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">{t('staffDashboard.offlineTitle')}</h3>
              <p className="text-sm text-amber-800 mt-1">{t('staffDashboard.offlineMessage')}</p>
              <button
                onClick={() => {
                  refetchDashboard();
                  refetchCleaning();
                }}
                className="mt-3 px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                {t('staffDashboard.retryLoad')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Skeleton */}
      {isLoading && !isOffline && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-bold text-neutral-800 animate-pulse bg-neutral-200 h-6 w-48 rounded"></h2>
            <p className="text-sm text-neutral-500 mt-2 animate-pulse bg-neutral-100 h-4 w-64 rounded"></p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, idx) => (
              <div key={idx} className="bg-neutral-100 h-40 rounded-xl animate-pulse"></div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {!isLoading && (
        <>
          {/* Section header */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-neutral-800">{t('staffDashboard.question')}</h2>
            <p className="text-sm text-neutral-500 mt-0.5">{t('staffDashboard.subtitle')}</p>
          </div>

          {/* Tile grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {tiles.map((tile, idx) => (
              <div
                key={tile.id}
                className="animate-fade-in"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <StaffTaskTile
                  icon={tile.icon}
                  label={t(tile.labelKey)}
                  to={tile.to}
                  count={tile.count}
                  status={tile.status}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
